import { useCallback, useMemo, useState } from 'react';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import { Box, Slider, Stack } from '@mui/material';
import Cropper, { type Area, type Point } from 'react-easy-crop';

const CROPPER_HEIGHT = '35vh';

type ImageCropperProps = {
  height?: string;
  image: string;
  onCrop: (area: Area) => void;
  cropShape: 'round' | 'rect';
  aspectRatio: number;
};

type PreviewProps = {
  croppedArea: Area;
  image: string;
  rounded?: boolean;
};

const FRAME = { rect: { w: 320, h: 128 }, round: 150 };

export const Preview = ({ croppedArea, image, rounded }: PreviewProps) => {
  const { frameWidth, frameHeight, transform, needFullWidth } = useMemo(() => {
    const isPercent = croppedArea.width <= 100 && croppedArea.height <= 100;

    const frameWidth = rounded ? FRAME.round : FRAME.rect.w;
    const frameHeight = rounded ? FRAME.round : FRAME.rect.h;

    if (isPercent) {
      const scale = 100 / croppedArea.width;
      return {
        frameWidth,
        frameHeight,
        needFullWidth: true,
        transform: `translate3d(${-croppedArea.x * scale}%, ${-croppedArea.y * scale}%, 0) scale3d(${scale},${scale},1)`,
      };
    }

    const scale = Math.min(
      frameWidth / croppedArea.width,
      frameHeight / croppedArea.height
    );

    return {
      frameWidth,
      frameHeight,
      needFullWidth: false,
      transform: `translate3d(${-croppedArea.x * scale}px, ${-croppedArea.y * scale}px, 0) scale3d(${scale},${scale},1)`,
    };
  }, [croppedArea, rounded]);

  return (
    <Stack direction="row" justifyContent="center" alignItems="center">
      <Box
        sx={{
          position: 'relative',
          width: frameWidth,
          height: frameHeight,
          overflow: 'hidden',
          bgcolor: 'neutral.50',
          borderRadius: rounded ? '50%' : 0,
          '& img': {
            position: 'absolute',
            top: 0,
            left: 0,
            transform,
            transformOrigin: 'top left',
            width: needFullWidth ? 'calc(100% + 0.5px)' : 'auto',
            height: 'auto',
          },
        }}
      >
        <img src={image} alt="preview" />
      </Box>
    </Stack>
  );
};

export const ImageCropper = ({
  height = CROPPER_HEIGHT,
  image,
  onCrop: onCropCallback,
  cropShape,
  aspectRatio,
}: ImageCropperProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [zoom, setZoom] = useState<number>(1);

  const onCropComplete = useCallback(
    (_: unknown, croppedAreaPixels: Area) => {
      onCropCallback(croppedAreaPixels);
    },
    [onCropCallback]
  );

  const handleCropAreaChange = useCallback((crop: Area) => {
    setCroppedArea(crop);
  }, []);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
        }}
      >
        <Cropper
          aspect={aspectRatio}
          crop={crop}
          cropShape={cropShape}
          image={image}
          maxZoom={4}
          objectFit="vertical-cover"
          onCropAreaChange={handleCropAreaChange}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          zoom={zoom}
        />
      </Box>
      <Stack spacing={3} direction="row" sx={{ alignItems: 'center' }}>
        <PhotoSizeSelectLargeIcon />
        <Slider
          aria-labelledby="Zoom"
          max={4}
          min={1}
          onChange={(_, zoom) => setZoom(Number(zoom))}
          step={0.1}
          value={zoom}
        />
        <PhotoSizeSelectLargeIcon fontSize="large" />
      </Stack>
      {croppedArea && (
        <Preview
          croppedArea={croppedArea}
          rounded={cropShape === 'round'}
          image={image}
        />
      )}
    </Stack>
  );
};
